import { useEffect, useMemo, useRef, useState } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useRoute, useLocation, Link } from "wouter";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, MapPin, MessageSquare, Send, User, ImageIcon, DollarSign, Loader2, Lock, BarChart2 } from "lucide-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { SaleListing, SaleListingStatus, SaleMessage } from "@shared/schema";

type MarketplaceListing = SaleListing & {
  propertyName: string;
  propertyAddress: string | null;
  latitude: number | null;
  longitude: number | null;
  sellerName: string | null;
};

type ListingSummary = {
  propertyName: string | null;
  propertyAddress: string | null;
  bedrooms: number;
  bathrooms: number;
  sqft: number | null;
  yearBuilt: number | null;
  hasUnderwriting: boolean;
  askingPrice: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  monthlyMortgage: number;
  monthlyCashflow: number;
  annualCashflow: number;
  annualNOI: number;
  capRate: number;
  grossYield: number;
};

type CurrentUser = { id: number; email: string; name: string | null; isAdmin?: boolean };

type InquirySummary = {
  id: number;
  listingId: number;
  sellerId: number;
  buyerId: number;
  lastMessagePreview: string | null;
  lastMessageAt: string;
  unreadCount: number;
  buyerDisplayName: string | null;
  sellerDisplayName: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  under_contract: "Under Contract",
  sold: "Sold",
};

const STATUS_COLOR: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-200",
  under_contract: "bg-amber-100 text-amber-800 border-amber-200",
  sold: "bg-gray-100 text-gray-700 border-gray-200",
};

function fmtMoney(n: number, opts: Intl.NumberFormatOptions = {}): string {
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0, ...opts }).format(n);
}

function fmtPct(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)}%`;
}

function MetricTile({ label, value, sub, testId }: { label: string; value: string; sub?: string; testId?: string }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4" data-testid={testId}>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-semibold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function ChatPanel({
  inquiryId, currentUserId, otherPartyName,
}: {
  inquiryId: number;
  currentUserId: number;
  otherPartyName: string;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: messages, isLoading } = useQuery<SaleMessage[]>({
    queryKey: ["/api/app/sell/inquiries", inquiryId, "messages"],
    refetchInterval: 8000,
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages?.length]);

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await apiRequest("POST", `/api/app/sell/inquiries/${inquiryId}/messages`, { content });
      return res.json();
    },
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["/api/app/sell/inquiries", inquiryId, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/sell/inquiries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/notifications/count"] });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't send message", description: parseErrorMessage(err, "Try again"), variant: "destructive" });
    },
  });

  const handleSend = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    sendMutation.mutate(trimmed);
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white flex flex-col h-[480px]" data-testid="sell-chat-panel">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-blue-600" />
        <span className="font-medium text-gray-900">Chat with {otherPartyName}</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50" data-testid="chat-messages">
        {isLoading ? (
          <Skeleton className="h-20 w-2/3" />
        ) : (messages || []).length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No messages yet — send the first one below.</p>
        ) : (
          (messages || []).map((m) => {
            const mine = m.senderId === currentUserId;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-900"}`}
                  data-testid={`chat-message-${m.id}`}
                >
                  <p className="whitespace-pre-wrap break-words">{m.content}</p>
                  <p className={`text-[10px] mt-1 ${mine ? "text-blue-100" : "text-gray-400"}`}>
                    {new Date(m.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="p-3 border-t border-gray-100 flex gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type your message..."
          rows={2}
          data-testid="input-chat-message"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button onClick={handleSend} disabled={sendMutation.isPending || !draft.trim()} data-testid="button-send-chat">
          {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function ContactSellerCTA({
  listing, isActive, mode,
}: {
  listing: MarketplaceListing;
  isActive: boolean;
  mode: "sell" | "buy";
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const sellerLabel = listing.sellerName || "the seller";

  const { data: copyCheck } = useQuery<{ propertyId: number | null }>({
    queryKey: ["/api/app/underwriting/copy-check", "marketplace", listing.id],
    queryFn: async () => {
      const res = await fetch(`/api/app/underwriting/copy-check?sourceType=marketplace&sourceListingId=${listing.id}`, { credentials: "include" });
      return res.json();
    },
  });
  const alreadyCopied = copyCheck?.propertyId != null;

  const copyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "marketplace", sourceListingId: listing.id });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check", "marketplace", listing.id] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });

  const startMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/sell/listings/${listing.id}/inquiries`, {});
      return res.json();
    },
    onSuccess: (data: { id: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/sell/inquiries"] });
      const dest = mode === "buy"
        ? `/app/buy?tab=inquiries&inquiry=${data.id}`
        : `/app/sell?tab=inquiries&inquiry=${data.id}`;
      navigate(dest);
    },
    onError: (err: any) => {
      toast({ title: "Couldn't start chat", description: parseErrorMessage(err, "Try again"), variant: "destructive" });
    },
  });

  if (!isActive) {
    return (
      <div className="border border-gray-200 rounded-xl bg-gray-50 p-6 text-center" data-testid="contact-seller-disabled">
        <div className="w-12 h-12 mx-auto rounded-full bg-gray-200 flex items-center justify-center mb-3">
          <Lock className="h-6 w-6 text-gray-500" />
        </div>
        <h3 className="font-semibold text-gray-900">Listing no longer available</h3>
        <p className="text-sm text-gray-500 mt-1">
          This property is no longer accepting buyer inquiries.
        </p>
      </div>
    );
  }

  const inquireMode = mode === "buy";
  return (
    <div className="border border-gray-200 rounded-xl bg-white p-6 text-center" data-testid="contact-seller-card" data-tour="sell-detail-contact">
      <div className="w-12 h-12 mx-auto rounded-full bg-blue-50 flex items-center justify-center mb-3">
        <MessageSquare className="h-6 w-6 text-blue-600" />
      </div>
      <h3 className="font-semibold text-gray-900">Interested in this property?</h3>
      <p className="text-sm text-gray-500 mt-1 mb-4">
        Start a private conversation with {sellerLabel}. Your chat lives in <strong>My Inquiries</strong> on the {inquireMode ? "Buy" : "Sell"} page, and you'll be notified of replies.
      </p>
      <div className="flex flex-col items-stretch gap-2">
        <Button
          onClick={() => startMutation.mutate()}
          disabled={startMutation.isPending}
          data-testid="button-contact-seller"
          size="lg"
        >
          {startMutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting chat...</>
          ) : (
            <><MessageSquare className="h-4 w-4 mr-2" /> {inquireMode ? "Inquire about this property" : "Contact Seller"}</>
          )}
        </Button>
        <Button
          variant={alreadyCopied ? "default" : "outline"}
          size="lg"
          onClick={() => {
            if (alreadyCopied && copyCheck?.propertyId) {
              navigate(`/app/underwriting/${copyCheck.propertyId}`);
            } else {
              copyMutation.mutate();
            }
          }}
          disabled={copyMutation.isPending}
          data-testid="button-underwrite-myself-marketplace"
        >
          {copyMutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Copying...</>
          ) : alreadyCopied ? (
            <><BarChart2 className="h-4 w-4 mr-2" /> Review in Underwriting</>
          ) : (
            <><BarChart2 className="h-4 w-4 mr-2" /> Underwrite myself</>
          )}
        </Button>
      </div>
    </div>
  );
}

function SellerView({
  listing, currentUserId, initialInquiryId,
}: {
  listing: MarketplaceListing;
  currentUserId: number;
  initialInquiryId: number | null;
}) {
  const [activeId, setActiveId] = useState<number | null>(initialInquiryId);
  const { data: inquiries, isLoading } = useQuery<InquirySummary[]>({
    queryKey: ["/api/app/sell/inquiries"],
    refetchInterval: 15000,
  });

  const listingInquiries = (inquiries || []).filter((i) => i.listingId === listing.id);
  const active = listingInquiries.find((i) => i.id === activeId) || listingInquiries[0];

  useEffect(() => {
    if (activeId === null && listingInquiries.length > 0) {
      setActiveId(listingInquiries[0].id);
    }
  }, [listingInquiries.length, activeId, listingInquiries]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="seller-conversations">
      <div className="md:col-span-1 border border-gray-200 rounded-xl bg-white overflow-hidden flex flex-col h-[480px]" data-tour="sell-detail-chat">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-medium text-gray-900">Buyer Inquiries</h3>
          <p className="text-xs text-gray-500">{listingInquiries.length} {listingInquiries.length === 1 ? "conversation" : "conversations"}</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : listingInquiries.length === 0 ? (
            <p className="p-4 text-sm text-gray-500 text-center">No buyer inquiries yet. They'll show up here when buyers reach out.</p>
          ) : (
            listingInquiries.map((i) => {
              const isActive = active?.id === i.id;
              return (
                <button
                  key={i.id}
                  onClick={() => setActiveId(i.id)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${isActive ? "bg-blue-50 border-l-4 border-l-blue-600" : ""}`}
                  data-testid={`inquiry-row-${i.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 truncate">{i.buyerDisplayName || `Buyer #${i.buyerId}`}</span>
                    {i.unreadCount > 0 && (
                      <Badge className="bg-blue-600 text-white border-0 text-[10px]" data-testid={`unread-badge-${i.id}`}>
                        {i.unreadCount}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">{i.lastMessagePreview || "No messages yet"}</p>
                </button>
              );
            })
          )}
        </div>
      </div>
      <div className="md:col-span-2">
        {active ? (
          <ChatPanel
            inquiryId={active.id}
            currentUserId={currentUserId}
            otherPartyName={active.buyerDisplayName || `Buyer #${active.buyerId}`}
          />
        ) : (
          <div className="border border-gray-200 rounded-xl bg-white p-8 text-center text-gray-500 h-[480px] flex flex-col items-center justify-center" data-testid="no-conversation-selected">
            <MessageSquare className="h-10 w-10 text-gray-300 mb-2" />
            <p className="text-sm">Select a conversation to view messages.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AppSellDetail() {
  const [matchSell, sellParams] = useRoute("/app/sell/:id");
  const [matchBuy, buyParams] = useRoute("/app/buy/listing/:id");
  const [, navigate] = useLocation();
  const params = matchBuy ? buyParams : sellParams;
  const mode: "sell" | "buy" = matchBuy ? "buy" : "sell";
  const id = params?.id ? parseInt(params.id, 10) : NaN;
  const backHref = mode === "buy" ? "/app/buy" : "/app/sell";
  const backLabel = mode === "buy" ? "Back to Buy" : "Back to Sell";

  const initialInquiryId = useMemo(() => {
    if (typeof window === "undefined") return null;
    const u = new URL(window.location.href);
    const v = u.searchParams.get("inquiry");
    return v ? parseInt(v, 10) : null;
  }, []);

  const { data: meResp } = useQuery<{ user: CurrentUser }>({ queryKey: ["/api/app/auth/me"] });
  const currentUser = meResp?.user;

  const { data: listing, isLoading, error } = useQuery<MarketplaceListing>({
    queryKey: ["/api/app/sell/marketplace", id],
    enabled: Number.isFinite(id),
  });
  usePageTitle(listing?.propertyAddress || "Listing");

  const { data: summary } = useQuery<ListingSummary>({
    queryKey: ["/api/app/sell/marketplace", id, "summary"],
    enabled: Number.isFinite(id),
  });

  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const photos = listing?.photos || [];
  const cover = photos[activePhotoIdx] || photos[0];

  if (!Number.isFinite(id)) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto px-4 py-10 text-center text-gray-500">Invalid listing.</div>
      </AppLayout>
    );
  }

  if (isLoading || !currentUser) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-20" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error || !listing) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto px-4 py-10 text-center text-gray-500" data-testid="listing-not-found">
          Listing not found, or it's no longer available.
          <div className="mt-4">
            <Link href={backHref}><Button variant="outline" data-testid="button-back-listings"><ArrowLeft className="h-4 w-4 mr-2" /> {backLabel}</Button></Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  const owned = listing.userId === currentUser.id;
  const status = listing.status as SaleListingStatus;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <ViewOnlyBanner area="properties" />
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" asChild data-testid="button-back">
            <Link href={backHref}><ArrowLeft className="h-4 w-4 mr-2" /> {backLabel}</Link>
          </Button>
          <div className="flex items-center gap-2">
            <Badge className={`${STATUS_COLOR[status] || STATUS_COLOR.active} border`} data-testid="badge-status">
              {STATUS_LABEL[status] || status}
            </Badge>
            <ExplainerVideoButton pageKey="sell-detail" />
            <GuideButton pageKey="sell-detail" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="sell-detail" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="md:col-span-2 space-y-3">
            <div className="bg-gray-100 rounded-xl overflow-hidden h-80 flex items-center justify-center" data-testid="hero-image">
              {cover ? (
                <img src={cover} alt={listing.propertyName} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="h-12 w-12 text-gray-300" />
              )}
            </div>
            {photos.length > 1 && (
              <div className="grid grid-cols-6 gap-2">
                {photos.map((url, i) => (
                  <button
                    key={url + i}
                    onClick={() => setActivePhotoIdx(i)}
                    className={`aspect-square rounded overflow-hidden border ${i === activePhotoIdx ? "ring-2 ring-blue-500" : ""}`}
                    data-testid={`thumb-${i}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="text-listing-name" data-tour="sell-detail-heading">
                {listing.propertyName}
              </h1>
              {listing.propertyAddress && (
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> {listing.propertyAddress}
                </p>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
              <p className="text-xs text-blue-700 uppercase tracking-wide">Asking Price</p>
              <p className="text-3xl font-bold text-gray-900" data-testid="text-asking-price">
                {fmtMoney(parseFloat(String(listing.askingPrice)))}
              </p>
            </div>

            {listing.sellerName && (
              <div className="text-sm text-gray-700 flex items-center gap-2" data-testid="text-seller">
                <User className="h-4 w-4 text-gray-400" /> Listed by {listing.sellerName}
              </div>
            )}

            {listing.description && (
              <div className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="text-description">
                {listing.description}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-gray-200 rounded-xl bg-white p-6" data-tour="sell-detail-model">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-600" /> Property & Financial Snapshot
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Property details and financials are pulled directly from the seller's PadPulse property record.
            </p>
            {!summary ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <MetricTile
                    label="Bedrooms"
                    value={summary.bedrooms ? String(summary.bedrooms) : "—"}
                    sub="From property record"
                    testId="metric-bedrooms"
                  />
                  <MetricTile
                    label="Bathrooms"
                    value={summary.bathrooms ? String(summary.bathrooms) : "—"}
                    sub="From property record"
                    testId="metric-bathrooms"
                  />
                  <MetricTile
                    label="Square Feet"
                    value={summary.sqft ? summary.sqft.toLocaleString() : "—"}
                    testId="metric-sqft"
                  />
                  <MetricTile
                    label="Asking Price"
                    value={fmtMoney(summary.askingPrice)}
                    sub={summary.yearBuilt ? `Built ${summary.yearBuilt}` : undefined}
                    testId="metric-asking"
                  />
                </div>
                {summary.hasUnderwriting ? (
                  <>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Projected Performance (from seller's underwriting)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <MetricTile label="Monthly Income" value={fmtMoney(summary.monthlyIncome)} testId="metric-income" />
                      <MetricTile label="Monthly Expenses" value={fmtMoney(summary.monthlyExpenses)} sub={`incl. ${fmtMoney(summary.monthlyMortgage)} mortgage`} testId="metric-expenses" />
                      <MetricTile label="Monthly Cash Flow" value={fmtMoney(summary.monthlyCashflow)} sub={`${fmtMoney(summary.annualCashflow)} annually`} testId="metric-cashflow" />
                      <MetricTile label="Annual NOI" value={fmtMoney(summary.annualNOI)} sub="Net Operating Income" testId="metric-noi" />
                      <MetricTile label="Cap Rate (at asking)" value={fmtPct(summary.capRate)} testId="metric-cap-rate" />
                      <MetricTile label="Gross Yield" value={fmtPct(summary.grossYield)} sub="Annual rent / asking price" testId="metric-gross-yield" />
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded p-4" data-testid="no-underwriting-notice">
                    The seller hasn't published a PadPulse underwriting analysis for this property yet, so projected performance numbers are unavailable. Reach out to the seller for more details.
                  </div>
                )}
              </>
            )}
          </div>

          {owned ? (
            <div className="border border-gray-200 rounded-xl bg-white p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" /> Buyer Inquiries
              </h2>
              <p className="text-sm text-gray-500 mb-4">All conversations with buyers interested in this property.</p>
              <SellerView listing={listing} currentUserId={currentUser.id} initialInquiryId={initialInquiryId} />
            </div>
          ) : (
            <ContactSellerCTA listing={listing} isActive={status === "active"} mode={mode} />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
